import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  return (
    <div className="bg-black text-white">
      {/* Navegación */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-sm' : 'bg-transparent'}`}>
        <div className="px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <div className="text-xl md:text-2xl font-bold tracking-wider">
            SOUL
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('que-es-soul')} className="text-white/80 hover:text-white transition-colors text-sm tracking-wide whitespace-nowrap cursor-pointer">
              Qué es Soul
            </button>
            <button onClick={() => scrollToSection('entradas')} className="text-white/80 hover:text-white transition-colors text-sm tracking-wide whitespace-nowrap cursor-pointer">
              Entradas
            </button>
            <button onClick={() => scrollToSection('como-funciona')} className="text-white/80 hover:text-white transition-colors text-sm tracking-wide whitespace-nowrap cursor-pointer">
              Cómo Funciona
            </button>
            <a href="https://pagos.soulfeelthenight.site/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#A855F7] to-[#EC4899] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] transition-all whitespace-nowrap cursor-pointer">
              🎟️ Comprar Entrada
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-black/98 backdrop-blur-sm border-t border-white/10">
            <div className="px-4 py-6 space-y-4">
              <button onClick={() => scrollToSection('que-es-soul')} className="block w-full text-left text-white/80 hover:text-white transition-colors text-base py-2 cursor-pointer">
                Qué es Soul
              </button>
              <button onClick={() => scrollToSection('entradas')} className="block w-full text-left text-white/80 hover:text-white transition-colors text-base py-2 cursor-pointer">
                Entradas
              </button>
              <button onClick={() => scrollToSection('como-funciona')} className="block w-full text-left text-white/80 hover:text-white transition-colors text-base py-2 cursor-pointer">
                Cómo Funciona
              </button>
              <a href="https://pagos.soulfeelthenight.site/" target="_blank" rel="noopener noreferrer" className="block bg-gradient-to-r from-[#A855F7] to-[#EC4899] text-white px-6 py-3 rounded-full text-sm font-medium text-center whitespace-nowrap cursor-pointer">
                🎟️ Comprar Entrada
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero / Portada */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=dark%20atmospheric%20nightclub%20interior%20with%20dramatic%20purple%20and%20pink%20neon%20lighting%20creating%20moody%20ambiance%2C%20empty%20dance%20floor%20with%20fog%20and%20haze%20illuminated%20by%20vibrant%20violet%20and%20magenta%20light%20beams%2C%20modern%20electronic%20music%20venue%20with%20geometric%20patterns%20and%20bokeh%20light%20effects%2C%20cinematic%20photography%20with%20deep%20blacks%20and%20strong%20color%20contrast%2C%20professional%20event%20space%20with%20warm%20orange%20accent%20lights%20in%20distant%20background%2C%20artistic%20composition%20with%20depth%20and%20mystery%20through%20layered%20lighting%20and%20atmospheric%20smoke&width=1920&height=1080&seq=soul-hero-main-v3-new-001&orientation=landscape" 
            alt="SOUL Experience" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90"></div>
        </div>
        
        <div className="relative z-10 px-4 md:px-6 max-w-7xl w-full">
          <div className="max-w-2xl">
            <h1 className="text-[60px] md:text-[140px] font-black tracking-tight leading-none mb-2 text-white">
              SOUL
            </h1>
            <p className="text-2xl md:text-4xl italic font-serif text-[#FFD700] tracking-wide mb-10 md:mb-20">
              FEEL THE NIGHT&nbsp;
            </p>
            
            <h2 className="text-3xl md:text-5xl font-bold leading-tight mb-6 md:mb-8 text-white max-w-xl">
              LA NOCHE NO SE EXPLICA<br />
              SE SIENTE
            </h2>
            
            <p className="text-base md:text-xl text-white/90 mb-8 md:mb-10 max-w-lg leading-relaxed">
              Vive experiencias únicas donde la música, la energía y la conexión lo cambian todo.
            </p>
            
            <a href="https://pagos.soulfeelthenight.site/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#A855F7] to-[#EC4899] text-white px-8 md:px-10 py-3 md:py-4 rounded-full text-sm md:text-base font-semibold hover:scale-105 transition-transform inline-flex items-center gap-3 whitespace-nowrap cursor-pointer animate-pulse hover:animate-none">
              🎟️ Compra tu entrada ahora
            </a>
            <p className="text-xs md:text-sm text-white/60 mt-4">
              Acceso digital con QR exclusivo para ingresar al evento
            </p>
          </div>
        </div>
        
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-2xl md:text-3xl text-white/50"></i>
        </div>
      </section>

      {/* Qué es SOUL */}
      <section id="que-es-soul" className="bg-[#0A0A0A] py-20 md:py-44">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-16">
            <div className="md:col-span-2 relative">
              <div className="absolute left-0 top-0 w-1 h-24 md:h-48 bg-gradient-to-b from-[#A855F7] to-[#EC4899]"></div>
              <div className="pl-6 md:pl-12">
                <h2 className="text-4xl md:text-7xl font-bold text-white mb-4">
                  Más que una fiesta.
                </h2>
                <p className="text-3xl md:text-6xl italic font-serif text-[#EC4899]">
                  Es un estado.
                </p>
              </div>
            </div>
            
            <div className="md:col-span-3">
              <div className="space-y-6 md:space-y-8 text-lg md:text-2xl font-light text-[#D1D1D1] leading-[1.8]">
                <p>
                  SOUL es una experiencia nocturna creada para quienes no solo salen, sino <span className="text-[#A855F7] font-medium">viven la noche</span>.
                </p>
                <p>
                  Cada evento está diseñado para <span className="text-[#A855F7] font-medium">conectar</span> música, personas y <span className="text-[#A855F7] font-medium">emociones</span> en un solo momento irrepetible.
                </p>
                <p className="text-2xl md:text-3xl font-bold text-white">
                  Aquí no vienes solo a bailar.<br />
                  Vienes a <span className="text-[#A855F7]">sentir</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* La Experiencia */}
      <section id="experiencia" className="bg-[#0F0F0F] py-20 md:py-48">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <h2 className="text-4xl md:text-6xl font-bold text-center text-white mb-12 md:mb-24">
            ¿Qué hace diferente a SOUL?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 mb-12 md:mb-20">
            <div className="bg-black border border-transparent bg-gradient-to-br from-[#A855F7]/20 via-transparent to-[#EC4899]/20 rounded-3xl p-8 md:p-16 hover:shadow-[0_0_60px_rgba(168,85,247,0.4)] transition-all group cursor-pointer">
              <div className="text-4xl md:text-6xl mb-4 md:mb-6">✨</div>
              <h3 className="text-2xl md:text-3xl font-bold text-white">
                Curaduría musical envolvente
              </h3>
            </div>
            
            <div className="bg-black border border-transparent bg-gradient-to-br from-[#A855F7]/20 via-transparent to-[#EC4899]/20 rounded-3xl p-8 md:p-16 hover:shadow-[0_0_60px_rgba(168,85,247,0.4)] transition-all group cursor-pointer">
              <div className="text-4xl md:text-6xl mb-4 md:mb-6">🔥</div>
              <h3 className="text-2xl md:text-3xl font-bold text-white">
                Ambientes diseñados para la noche
              </h3>
            </div>
            
            <div className="bg-black border border-transparent bg-gradient-to-br from-[#A855F7]/20 via-transparent to-[#EC4899]/20 rounded-3xl p-8 md:p-16 hover:shadow-[0_0_60px_rgba(168,85,247,0.4)] transition-all group cursor-pointer">
              <div className="text-4xl md:text-6xl mb-4 md:mb-6">🖤</div>
              <h3 className="text-2xl md:text-3xl font-bold text-white">
                Público que vibra en la misma frecuencia
              </h3>
            </div>
            
            <div className="bg-black border border-transparent bg-gradient-to-br from-[#A855F7]/20 via-transparent to-[#EC4899]/20 rounded-3xl p-8 md:p-16 hover:shadow-[0_0_60px_rgba(168,85,247,0.4)] transition-all group cursor-pointer">
              <div className="text-4xl md:text-6xl mb-4 md:mb-6">⚡</div>
              <h3 className="text-2xl md:text-3xl font-bold text-white">
                Momentos que no se repiten
              </h3>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-2xl md:text-4xl italic font-serif text-[#E8E8E8] leading-relaxed">
              Cada edición es única.<br />
              Cada noche deja huella.
            </p>
          </div>
        </div>
      </section>

      {/* Entradas SOUL - Nueva Sección */}
      <section id="entradas" className="bg-black py-20 md:py-44">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-12 md:mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-4 md:mb-6">
              Entradas SOUL
            </h2>
            <p className="text-base md:text-xl text-[#CCCCCC] max-w-3xl mx-auto leading-relaxed">
              Todas las entradas incluyen los mismos beneficios. El precio varía según la etapa de preventa.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-20">
            {/* Preventa 1 */}
            <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-3xl p-8 md:p-10 hover:border-[#A855F7]/50 transition-all">
              <div className="text-center mb-6 md:mb-8">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4">Preventa 1</h3>
                <div className="mb-4 md:mb-6">
                  <span className="text-4xl md:text-6xl font-black text-white">S/ 25</span>
                </div>
                <p className="text-sm md:text-base text-[#CCCCCC]">
                  Acceso general al evento SOUL
                </p>
              </div>
              
              <div className="space-y-3 md:space-y-4 mb-8 md:mb-10">
                <div className="flex items-start gap-3">
                  <span className="text-[#00FF88] text-lg md:text-xl mt-1">✓</span>
                  <span className="text-sm text-[#CCCCCC]">Acceso completo al evento</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#00FF88] text-lg md:text-xl mt-1">✓</span>
                  <span className="text-sm text-[#CCCCCC]">QR digital de ingreso</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#00FF88] text-lg md:text-xl mt-1">✓</span>
                  <span className="text-sm text-[#CCCCCC]">Entrada válida para una persona</span>
                </div>
              </div>
              
              <a href="https://pagos.soulfeelthenight.site/" target="_blank" rel="noopener noreferrer" className="block w-full bg-gradient-to-r from-[#A855F7] to-[#EC4899] text-white py-3 md:py-4 rounded-full text-sm md:text-base font-semibold hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] transition-all text-center whitespace-nowrap cursor-pointer">
                Comprar entrada
              </a>
            </div>

            {/* Preventa 2 - Destacada */}
            <div className="bg-gradient-to-br from-[#A855F7]/10 to-[#EC4899]/10 border-2 border-[#A855F7] rounded-3xl p-8 md:p-10 relative md:transform md:scale-105 shadow-[0_0_50px_rgba(168,85,247,0.3)]">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#A855F7] to-[#EC4899] text-white px-4 md:px-6 py-1 md:py-1.5 rounded-full text-xs font-bold whitespace-nowrap">
                MÁS POPULAR
              </div>
              
              <div className="text-center mb-6 md:mb-8">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4">Preventa 2</h3>
                <div className="mb-4 md:mb-6">
                  <span className="text-4xl md:text-6xl font-black text-white">S/ 30</span>
                </div>
                <p className="text-sm md:text-base text-[#CCCCCC]">
                  Acceso general al evento SOUL
                </p>
              </div>
              
              <div className="space-y-3 md:space-y-4 mb-8 md:mb-10">
                <div className="flex items-start gap-3">
                  <span className="text-[#00FF88] text-lg md:text-xl mt-1">✓</span>
                  <span className="text-sm text-[#CCCCCC]">Acceso completo al evento</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#00FF88] text-lg md:text-xl mt-1">✓</span>
                  <span className="text-sm text-[#CCCCCC]">QR digital de ingreso</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#00FF88] text-lg md:text-xl mt-1">✓</span>
                  <span className="text-sm text-[#CCCCCC]">Entrada válida para una persona</span>
                </div>
              </div>
              
              <a href="https://pagos.soulfeelthenight.site/" target="_blank" rel="noopener noreferrer" className="block w-full bg-gradient-to-r from-[#A855F7] to-[#EC4899] text-white py-3 md:py-4 rounded-full text-sm md:text-base font-semibold hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] transition-all text-center whitespace-nowrap cursor-pointer">
                Comprar entrada
              </a>
            </div>

            {/* Preventa 3 */}
            <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-3xl p-8 md:p-10 hover:border-[#A855F7]/50 transition-all">
              <div className="text-center mb-6 md:mb-8">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4">Preventa 3</h3>
                <div className="mb-4 md:mb-6">
                  <span className="text-4xl md:text-6xl font-black text-white">S/ 35</span>
                </div>
                <p className="text-sm md:text-base text-[#CCCCCC]">
                  Acceso general al evento SOUL
                </p>
              </div>
              
              <div className="space-y-3 md:space-y-4 mb-8 md:mb-10">
                <div className="flex items-start gap-3">
                  <span className="text-[#00FF88] text-lg md:text-xl mt-1">✓</span>
                  <span className="text-sm text-[#CCCCCC]">Acceso completo al evento</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#00FF88] text-lg md:text-xl mt-1">✓</span>
                  <span className="text-sm text-[#CCCCCC]">QR digital de ingreso</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#00FF88] text-lg md:text-xl mt-1">✓</span>
                  <span className="text-sm text-[#CCCCCC]">Entrada válida para una persona</span>
                </div>
              </div>
              
              <a href="https://pagos.soulfeelthenight.site/" target="_blank" rel="noopener noreferrer" className="block w-full bg-gradient-to-r from-[#A855F7] to-[#EC4899] text-white py-3 md:py-4 rounded-full text-sm md:text-base font-semibold hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] transition-all text-center whitespace-nowrap cursor-pointer">
                Comprar entrada
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Entradas - Sección anterior simplificada */}
      <section className="grid grid-cols-1 md:grid-cols-2 min-h-[600px] md:min-h-[700px]">
        <div className="bg-black flex items-center justify-center px-6 md:px-24 py-12 md:py-20">
          <div className="max-w-xl">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 md:mb-10">
              Consigue tu acceso a SOUL
            </h2>
            <p className="text-base md:text-xl text-[#CCCCCC] mb-8 md:mb-12 leading-relaxed">
              Compra tu entrada de forma rápida y segura. Al finalizar recibirás un QR digital único que será tu acceso al evento.
            </p>
            
            <div className="space-y-4 md:space-y-5 mb-10 md:mb-16">
              <div className="flex items-center gap-3 md:gap-4 text-base md:text-lg text-[#CCCCCC]">
                <span className="text-[#00FF88] text-xl md:text-2xl">✔️</span>
                <span>Compra 100% online</span>
              </div>
              <div className="flex items-center gap-3 md:gap-4 text-base md:text-lg text-[#CCCCCC]">
                <span className="text-[#00FF88] text-xl md:text-2xl">✔️</span>
                <span>QR personal e intransferible</span>
              </div>
              <div className="flex items-center gap-3 md:gap-4 text-base md:text-lg text-[#CCCCCC]">
                <span className="text-[#00FF88] text-xl md:text-2xl">✔️</span>
                <span>Acceso rápido sin colas</span>
              </div>
            </div>
            
            <a href="https://pagos.soulfeelthenight.site/" target="_blank" rel="noopener noreferrer" className="block w-full bg-gradient-to-r from-[#EC4899] to-[#F97316] text-white py-4 md:py-6 rounded-[40px] text-lg md:text-xl font-bold hover:shadow-[0_0_50px_rgba(236,72,153,0.6)] transition-all whitespace-nowrap cursor-pointer text-center">
              🎟️ Comprar entrada
            </a>
          </div>
        </div>
        
        <div className="relative overflow-hidden min-h-[400px] md:min-h-0">
          <img 
            src="https://readdy.ai/api/search-image?query=energetic%20nightclub%20crowd%20dancing%20with%20hands%20raised%20under%20purple%20and%20pink%20neon%20lights%20with%20atmospheric%20fog%2C%20dramatic%20lighting%20creating%20silhouettes%20of%20people%20enjoying%20music%2C%20modern%20electronic%20music%20festival%20indoor%20venue%20with%20vibrant%20atmosphere%2C%20people%20celebrating%20in%20dark%20ambient%20setting%20with%20vibrant%20violet%20and%20magenta%20light%20beams%20cutting%20through%20haze%2C%20professional%20event%20photography%20with%20motion%20blur%20effects%20and%20bokeh%2C%20immersive%20party%20atmosphere%20with%20warm%20orange%20accent%20lights%20in%20background&width=800&height=700&seq=soul-tickets-visual-v2-002&orientation=portrait" 
            alt="SOUL Experience" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#A855F7]/20 mix-blend-overlay"></div>
        </div>
      </section>

      {/* Cómo Funciona */}
      <section id="como-funciona" className="bg-gradient-to-b from-black to-[#1A1A1A] py-20 md:py-44">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <h2 className="text-4xl md:text-6xl font-bold text-center text-white mb-16 md:mb-32">
            Así de simple
          </h2>
          
          <div className="relative">
            <div className="hidden md:block absolute top-[56px] left-[70px] right-[70px] h-0.5 bg-[#A855F7]"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-8">
              {[
                { emoji: '1️⃣', text: 'Elige tu evento' },
                { emoji: '2️⃣', text: 'Compra tu entrada' },
                { emoji: '3️⃣', text: 'Recibe tu QR en tu correo' },
                { emoji: '4️⃣', text: 'Preséntalo en el ingreso' },
                { emoji: '5️⃣', text: 'Siente la noche', special: true }
              ].map((step, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className="w-24 h-24 md:w-[140px] md:h-[140px] rounded-full bg-black border-[3px] border-transparent bg-gradient-to-br from-[#A855F7] to-[#EC4899] flex items-center justify-center mb-6 md:mb-10 relative z-10">
                    <span className="text-3xl md:text-5xl font-bold text-white">{index + 1}</span>
                  </div>
                  <p className={`text-base md:text-lg text-center max-w-[120px] ${step.special ? 'italic text-[#EC4899] font-semibold' : 'text-[#CCCCCC]'}`}>
                    {step.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comunidad / Emocional */}
      <section className="relative min-h-[600px] md:h-[800px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=atmospheric%20concert%20crowd%20silhouettes%20with%20raised%20hands%20in%20purple%20and%20pink%20stage%20lighting%20with%20fog%2C%20emotional%20music%20festival%20moment%20with%20dramatic%20backlighting%20and%20haze%2C%20people%20connecting%20through%20music%20in%20dark%20venue%20with%20vibrant%20neon%20colors%20and%20warm%20orange%20accents%2C%20cinematic%20photography%20with%20shallow%20focus%20and%20bokeh%20effects%20creating%20dreamy%20atmosphere%2C%20intimate%20nightlife%20experience%20with%20violet%20and%20magenta%20ambient%20glow%2C%20artistic%20event%20photography%20capturing%20energy%20and%20emotion%20with%20light%20beams&width=1920&height=800&seq=soul-community-bg-v2-003&orientation=landscape" 
            alt="SOUL Community" 
            className="w-full h-full object-cover blur-sm"
          />
          <div className="absolute inset-0 bg-gradient-radial from-black/40 via-black/60 to-black/70"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 md:px-6 max-w-4xl">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-10 md:mb-16">
            Esta noche es para los que sienten.
          </h2>
          
          <div className="space-y-4 md:space-y-6 mb-12 md:mb-20 text-xl md:text-3xl italic font-serif text-[#E8E8E8] leading-[1.6]">
            <p>Para los que buscan algo más que luces.</p>
            <p>Para los que entienden que la noche también tiene alma.</p>
          </div>
          
          <div className="space-y-2">
            <p className="text-2xl md:text-4xl font-bold text-white">
              SOUL no es para todos.
            </p>
            <p className="text-2xl md:text-4xl font-bold text-[#EC4899]">
              Es para los que conectan.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="bg-black py-20 md:py-48 text-center">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-5xl md:text-8xl font-black text-white mb-8 md:mb-12 drop-shadow-[0_0_40px_rgba(168,85,247,0.6)]">
            La noche te está esperando
          </h2>
          <p className="text-xl md:text-3xl text-[#CCCCCC] mb-10 md:mb-16">
            No te quedes afuera de la próxima experiencia SOUL.
          </p>
          
          <a href="https://pagos.soulfeelthenight.site/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#A855F7] via-[#EC4899] to-[#F97316] text-white px-12 md:px-20 py-5 md:py-7 rounded-[44px] text-lg md:text-2xl font-bold hover:scale-105 hover:shadow-[0_0_60px_rgba(168,85,247,0.7)] transition-all inline-flex items-center gap-3 whitespace-nowrap cursor-pointer">
            🎟️ Compra tu entrada ahora
          </a>
          <p className="text-base md:text-xl italic font-serif text-[#999999] mt-6">
            FEEL THE NIGHT
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 md:mb-16">
            <div>
              <div className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">SOUL</div>
              <div className="space-y-2 md:space-y-3 text-sm text-[#666666] leading-[2.0]">
                <p>Experiencias</p>
                <p>Música</p>
                <p>Noche</p>
              </div>
            </div>
            
            <div></div>
            
            <div className="text-left md:text-right">
              <p className="text-sm uppercase tracking-[0.15em] text-[#999999] mb-4 md:mb-5">
                Síguenos en redes
              </p>
              <div className="flex gap-4 justify-start md:justify-end">
                <a href="https://www.instagram.com/soul.feelthenight?igsh=N3JlajFwd3NkNXUz&utm_source=qr" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center text-[#CCCCCC] hover:text-[#EC4899] transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-2xl md:text-3xl"></i>
                </a>
                <a href="https://www.tiktok.com/@soul.feelthenight?_r=1&_t=ZS-93Vn5J2VXKn" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center text-[#CCCCCC] hover:text-[#EC4899] transition-colors cursor-pointer">
                  <i className="ri-tiktok-line text-2xl md:text-3xl"></i>
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-[#1A1A1A] pt-6 md:pt-8 text-center">
            <p className="text-xs text-[#555555]">SOUL © 2025</p>
          </div>
        </div>
        
        <div className="hidden md:block absolute bottom-0 left-1/2 -translate-x-1/2 text-[240px] font-bold text-white/[0.03] pointer-events-none select-none">
          SOUL
        </div>
      </footer>
    </div>
  );
}
